<template lang="pug">
.c-stickers-page
  //- Header Section
  section.c-stickers-page__header
    .c-stickers-page__header-content
      h1.c-stickers-page__heading Stickers
      p.c-stickers-page__subtitle Manage sticker content and approvals

    .c-stickers-page__header-actions
      el-button.c-stickers-action-btn(@click="$router.push(`/manage-content/media/add-stickers-v2`)")
        fai(type="fal" icon="plus-circle").me-2
        span Add New

  //- Tab Navigation
  section.c-stickers-page__nav-section
    .c-stickers-nav
      button.c-stickers-nav-link(
        @click="!loading && headerFilter('ALL')"
        :class="{ 'c-stickers-nav-link--active': state.currentView === 'ALL' }"
        :disabled="loading"
      ) All
      button.c-stickers-nav-link(
        @click="!loading && headerFilter('Active')"
        :class="{ 'c-stickers-nav-link--active': state.currentView === 'Approved' }"
        :disabled="loading"
      ) Approved
      button.c-stickers-nav-link(
        @click="!loading && headerFilter('Draft')"
        :class="{ 'c-stickers-nav-link--active': state.currentView === 'Pending' }"
        :disabled="loading"
      ) Pending
      button.c-stickers-nav-link(
        @click="!loading && headerFilter('Archived')"
        :class="{ 'c-stickers-nav-link--active': state.currentView === 'Archived' }"
        :disabled="loading"
      ) Archived

  //- Organization Filter Indicator
  .c-stickers-org-filter(v-if="selectedOrganization && selectedOrganization.id")
    .c-stickers-org-filter__content
      span.c-stickers-org-filter__label Filtered by organization:
      span.c-stickers-org-filter__name {{ selectedOrganization.name }}
      el-button.c-stickers-org-filter__clear(
        type="text"
        @click="clearOrgFilter"
        :disabled="loading"
      ) Clear

  //- Content Section
  .c-stickers-content
    //- Filters and Actions
    section.c-stickers-filters-section
      .c-stickers-filters__bulk-actions
        span.c-stickers-filters__selected-count {{ selected.length }} selected
        VueMultiselect.c-stickers-filters__bulk-select(
          v-model="setAs"
          :multiple="false"
          :options="setAsOption"
          :searchable="false"
          :close-on-select="true"
          :show-labels="false"
          :disabled="selected.length === 0"
          label="label"
          placeholder="Set as"
          @select="bulkUpdate(setAs.value)"
        )

      .c-stickers-filters__controls
        el-input.c-stickers-filters__search(
          v-model="state.searchTerm"
          placeholder="Filter Stickers"
          @keyup="handleSearchKeyup"
          :disabled="loading"
        )
          template(#suffix)
            img(
              src="@/assets/icons/searchIcon.svg"
              @click="getItems"
            ).c-stickers-filters__search-icon

        el-button.c-stickers-filters__clear(
          v-show="state.searchTerm"
          @click="clearSearchAndRefresh"
          :disabled="loading"
        ) Clear

        VueMultiselect.c-stickers-filters__sort(
          v-model="state.sortOption"
          :multiple="false"
          :options="sortOptions"
          :searchable="false"
          :close-on-select="true"
          :show-labels="false"
          label="label"
          placeholder="Sort"
          @select="handleSortChange"
          :disabled="loading"
        )

    //- Table Section
    section.c-stickers-table-section
      //- Skeleton Loader
      template(v-if="loading")
        .c-skeleton-loader
          .c-skeleton-row(v-for="n in 10" :key="n")
            .c-skeleton-cell.c-skeleton-cell--checkbox
            .c-skeleton-cell.c-skeleton-cell--image
            .c-skeleton-cell.c-skeleton-cell--title
            .c-skeleton-cell.c-skeleton-cell--type
            .c-skeleton-cell.c-skeleton-cell--status
            .c-skeleton-cell.c-skeleton-cell--link

      //- Table
      el-table(
        v-else
        :data="items.items"
        @row-click="$router.push(`stickers-v2/${$event.id}`)"
        @selection-change="onCheckboxChange"
        style="width: 100%"
      ).clickable-rows
        el-table-column(width="15")

        el-table-column(type="selection" width="55")

        el-table-column(width="150" label="Image")
          template(#default="scope")
            .sticker-image-cell
              img.sticker-image(:src="scope.row.url" alt="Sticker")

        el-table-column(prop="title" label="Title" min-width="200")
          template(#default="scope")
            .lh-sm
              .font-black {{ scope.row.title }}

        el-table-column(label="Type" width="200")
          template(#default="scope")
            .lh-sm
              .font-medium Sticker

        el-table-column(label="Status" min-width="200")
          template(#default="scope")
            .status-badge(:class="getButtonClass(stickerState(scope.row))")
              | {{ stickerState(scope.row) }}

        el-table-column(label="Share Link" width="200")
          template(#default="scope")
            .share-link-cell(v-if="scope.row")
              span.share-link-text {{ getStickerShortUrl(scope.row) }}
              fai(
                type="fal"
                icon="copy"
                @click.stop="copyShareLink(scope.row)"
              ).copy-icon.size-16
            .share-link-cell(v-else)
              span.text-muted.fs-12 N/A

        el-table-column(width="15")

      //- Pagination and Page Size
      .c-stickers-pagination(v-if="items.items && items.items.length > 0")
        .c-stickers-pagination__page-size
          span.c-stickers-pagination__page-size-label Items per page:
          el-select.c-stickers-pagination__page-size-select(
            v-model="state.pageSize"
            @change="handleSizeChange"
            size="small"
          )
            el-option(
              v-for="size in [20, 30, 50]"
              :key="size"
              :label="size"
              :value="size"
            )

        .c-stickers-pagination__controls
          el-button.c-stickers-pagination__button(
            @click="handleCurrentChange(state.currentPage - 1)"
            :disabled="state.currentPage === 1 || loading"
          ) Prev
          .c-stickers-pagination__info
            span.c-stickers-pagination__page-number Page {{ state.currentPage }}
            span.c-stickers-pagination__item-count Showing {{ ((state.currentPage - 1) * state.pageSize) + 1 }}-{{ ((state.currentPage - 1) * state.pageSize) + items.items.length }}
          el-button.c-stickers-pagination__button(
            @click="handleCurrentChange(state.currentPage + 1)"
            :disabled="!state.hasMoreItems || loading"
          ) Next

  //- Error State
  .c-stickers-error(v-if="state.apiError && !loading")
    .c-stickers-error__icon
      fai(type="fal" icon="exclamation-triangle").size-48
    h3.c-stickers-error__title Error loading stickers
    p.c-stickers-error__text {{ state.apiError }}
    el-button.c-stickers-error__button(
      @click="getItems"
      type="primary"
    ) Retry
</template>

<script lang="ts">
import {
  reactive, onMounted, defineComponent, ref, watch, computed,
} from 'vue';
import VueMultiselect from 'vue-multiselect';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { ElMessage } from 'element-plus';
import DialogAddSticker from './DialogAddSticker.vue';
import shared from '../shared';

export default defineComponent({
  name: 'ManageContent_Media_Stickers',
  components: { DialogAddSticker, VueMultiselect },
  setup() {
    const {
      getStickers,
      updateSticker,
      loading,
      dialog,
      saveStickerStatusBulk,
    } = shared();

    // Initialize loading as true to show skeleton immediately
    loading.value = true;

    const items = reactive({ items: [] });
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const selectedOrganization = computed(() => store.getters['organizations/GET_SELECTED_ORGANIZATION']);

    const state = reactive({
      currentView: 'ALL',
      searchTerm: '',
      pageSize: 20,
      currentPage: 1,
      sortBy: { field: 'CreatedAt', asc: false },
      sortOption: null,
      activeFilters: ['ALL'],
      hasMoreItems: true,
      apiError: null,
    });

    const selected = ref([]);
    const ignoreNextUrlChange = ref(false);

    const sortOptions = [
      { label: 'Most Recent', value: { field: 'CreatedAt', asc: false } },
      { label: 'Oldest First', value: { field: 'CreatedAt', asc: true } },
      { label: 'Alphabetical (A-Z)', value: { field: 'Title', asc: true } },
      { label: 'Alphabetical (Z-A)', value: { field: 'Title', asc: false } },
    ];

    const setAsOption = [
      { label: 'Approved', value: 'Active' },
      { label: 'Pending', value: 'Pending' },
      { label: 'Archived', value: 'Archived' },
    ];
    const setAs = ref('');

    function handleSearchKeyup(event) {
      if (event.key === 'Enter') {
        getItems();
      }
    }

    function clearSearchAndRefresh() {
      state.searchTerm = '';
      state.currentPage = 1;
      state.activeFilters = ['ALL'];
      state.currentView = 'ALL';
      getItems();
    }

    function onCheckboxChange(checkedItems) {
      selected.value = checkedItems;
    }

    function handleSortChange(selectedSort) {
      if (selectedSort && selectedSort.value) {
        state.sortBy = selectedSort.value;
        state.sortOption = selectedSort;
        state.currentPage = 1;
        getItems();
      }
    }

    function handleSizeChange(newSize) {
      state.pageSize = newSize;
      state.currentPage = 1;
      getItems();
    }

    function handleCurrentChange(newPage) {
      state.currentPage = newPage;
      getItems();
    }

    function getCreator(itm) {
      if (itm.isSpotlight) {
        return 'Spotlight';
      }
      if (itm.entity === 'Organization') {
        return itm.organization.name;
      }
      return itm.creatorTitle;
    }

    function stickerState(itm) {
      switch (itm.status) {
        case 'Active':
          return 'Approved';
        case 'Draft':
        case null:
        case 'Pending':
          return 'Pending';
        case 'Archived':
          return 'Archived';
        default:
          return 'Pending';
      }
    }

    function getButtonClass(currentItem) {
      switch (currentItem) {
        case 'Approved':
          return 'status-badge--approved';
        case 'Pending':
        case 'Draft':
        case null:
          return 'status-badge--pending';
        case 'Archived':
          return 'status-badge--archived';
        default:
          return 'status-badge--pending';
      }
    }

    function headerFilter(filterBy) {
      state.currentView = filterBy;
      if (filterBy === 'ALL') {
        state.activeFilters = ['ALL'];
      } else {
        state.activeFilters = [filterBy];
      }

      state.currentPage = 1;
      getItems();
    }

    async function getItems() {
      try {
        loading.value = true;
        state.apiError = null;

        const skip = (state.currentPage - 1) * state.pageSize;
        const orderBy = state.sortBy ? state.sortBy.field : 'CreatedAt';
        const orderAsc = state.sortBy ? state.sortBy.asc : false;

        const organizationId = selectedOrganization.value ? selectedOrganization.value.id : '';

        const result = await getStickers(
          state.pageSize,
          skip,
          state.searchTerm,
          state.activeFilters,
          orderBy,
          orderAsc,
          organizationId,
        );

        updateUrlParams();

        if (!result) {
          items.items = [];
          state.hasMoreItems = false;
          state.apiError = 'API returned undefined or null result';
          return;
        }

        items.items = result;

        if (items.items.length === state.pageSize) {
          const nextPageResult = await getStickers(
            1,
            skip + state.pageSize,
            state.searchTerm,
            state.activeFilters,
            orderBy,
            orderAsc,
            organizationId,
          );

          state.hasMoreItems = nextPageResult && nextPageResult.length > 0;
        } else {
          state.hasMoreItems = false;
        }
      } catch (err) {
        console.error('Error fetching stickers:', err);
        state.apiError = 'Error loading stickers. Please try again later.';
      } finally {
        loading.value = false;
      }
    }

    function updateUrlParams() {
      ignoreNextUrlChange.value = true;

      const query = {};

      if (state.searchTerm) {
        query.search = state.searchTerm;
      }

      if (state.activeFilters.length > 0 && state.activeFilters[0] !== 'ALL') {
        query.filter = state.activeFilters[0];
      }

      if (state.sortBy) {
        query.sort = state.sortBy.field;
        if (state.sortBy.asc) {
          query.dir = 'asc';
        } else {
          query.dir = 'desc';
        }
      }

      if (state.currentPage > 1) {
        query.page = state.currentPage;
      }

      if (state.pageSize !== 20) {
        query.size = state.pageSize;
      }

      router.replace({ query }).catch(() => {});

      setTimeout(() => {
        ignoreNextUrlChange.value = false;
      }, 100);
    }

    function loadParamsFromUrl() {
      const { query } = route;

      if (query.search) {
        state.searchTerm = query.search.toString();
      } else {
        state.searchTerm = '';
      }

      if (query.filter) {
        const filterValue = query.filter.toString();
        if (['ALL', 'Approved', 'Pending', 'Archived'].includes(filterValue)) {
          state.activeFilters = [filterValue];
          state.currentView = filterValue;
        } else {
          state.activeFilters = ['ALL'];
          state.currentView = 'ALL';
        }
      } else {
        state.activeFilters = ['ALL'];
        state.currentView = 'ALL';
      }

      if (query.sort) {
        const sortField = query.sort.toString();
        if (['CreatedAt', 'Title'].includes(sortField)) {
          const sortDir = query.dir === 'asc';
          state.sortBy = { field: sortField, asc: sortDir };

          state.sortOption = sortOptions.find(
            (option) => option.value.field === sortField && option.value.asc === sortDir,
          );
        } else {
          state.sortBy = { field: 'CreatedAt', asc: false };
          state.sortOption = sortOptions.find(
            (option) => option.value.field === 'CreatedAt' && option.value.asc === false,
          );
        }
      } else {
        state.sortBy = { field: 'CreatedAt', asc: false };
        state.sortOption = sortOptions.find(
          (option) => option.value.field === 'CreatedAt' && option.value.asc === false,
        );
      }

      if (query.page) {
        try {
          const page = parseInt(query.page.toString(), 10);
          if (page > 0) {
            state.currentPage = page;
          } else {
            state.currentPage = 1;
          }
        } catch (e) {
          state.currentPage = 1;
        }
      } else {
        state.currentPage = 1;
      }

      if (query.size) {
        try {
          const size = parseInt(query.size.toString(), 10);
          if ([20, 30, 50].includes(size)) {
            state.pageSize = size;
          } else {
            state.pageSize = 20;
          }
        } catch (e) {
          state.pageSize = 20;
        }
      } else {
        state.pageSize = 20;
      }
    }

    async function bulkUpdate(type) {
      const bulkStickers = [];
      selected.value.forEach((element) => {
        const item = { id: element.id, status: type };
        bulkStickers.push(item);
      });
      await saveStickerStatusBulk(bulkStickers);
      await getItems();
      setAs.value = '';
    }

    async function saveItem(input) {
      await updateSticker(input);
      await getItems();
    }

    watch(
      () => route.query,
      () => {
        if (ignoreNextUrlChange.value) {
          return;
        }

        loadParamsFromUrl();
        getItems();
      },
      { deep: true },
    );

    watch(
      () => selectedOrganization.value,
      (newOrg, oldOrg) => {
        if (newOrg?.id !== oldOrg?.id) {
          state.currentPage = 1;
          getItems();
        }
      },
    );

    function clearOrgFilter() {
      store.dispatch('organizations/SET_SELECTED_ORGANIZATION', null);
      state.currentPage = 1;
      getItems();
    }

    function getStickerShortUrl(sticker) {
      if (!sticker) return '';
      const baseUrl = import.meta.env.VITE_WEB_BASE_URL || '';
      const identifier = sticker.slug || sticker.id;
      return `${baseUrl}/web/sticker/${identifier}`;
    }

    async function copyShareLink(sticker) {
      try {
        const url = getStickerShortUrl(sticker);

        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
          ElMessage.success('Share link copied to clipboard!');
        } else {
          // Fallback for older browsers or when clipboard API is not available
          const textArea = document.createElement('textarea');
          textArea.value = url;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();

          try {
            document.execCommand('copy');
            ElMessage.success('Share link copied to clipboard!');
          } catch (err) {
            console.error('Fallback copy failed:', err);
            ElMessage.error('Failed to copy share link');
          } finally {
            document.body.removeChild(textArea);
          }
        }
      } catch (error) {
        console.error('Failed to copy share link:', error);
        ElMessage.error('Failed to copy share link');
      }
    }

    onMounted(async () => {
      await store.dispatch('sticker/RESET_TO_DEFAULT');

      state.sortOption = sortOptions.find(
        (option) => option.value.field === state.sortBy.field && option.value.asc === state.sortBy.asc,
      );

      loadParamsFromUrl();

      await getItems();
    });

    return {
      items,
      loading,
      dialog,
      state,
      selected,
      sortOptions,
      setAsOption,
      setAs,
      selectedOrganization,
      getCreator,
      saveItem,
      stickerState,
      headerFilter,
      clearSearchAndRefresh,
      handleSortChange,
      onCheckboxChange,
      bulkUpdate,
      getButtonClass,
      handleSizeChange,
      handleCurrentChange,
      handleSearchKeyup,
      updateUrlParams,
      loadParamsFromUrl,
      clearOrgFilter,
      getStickerShortUrl,
      copyShareLink,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-stickers-page {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 2rem;
    margin-bottom: 0;
  }

  &__header-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__heading {
    font-size: 1.875rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5;
  }

  &__header-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  &__nav-section {
    margin-top: 0;
    margin-bottom: 0;
  }
}

// Action button styles
.c-stickers-action-btn {
  height: 36px;
  font-size: 14px;
  border-radius: 8px;
  background-color: white !important;
  border: 1.5px solid $color-purple-text !important;
  color: $color-purple-text !important;

  &:hover,
  &:focus {
    background-color: rgba($color-purple-text, 0.08) !important;
    border-color: color.adjust($color-purple-text, $lightness: -8%) !important;
    color: color.adjust($color-purple-text, $lightness: -8%) !important;
  }

  :deep(span) {
    font-weight: 500;
  }

  :deep(.me-2) {
    margin-right: 0.5rem;
  }
}

// Navigation Tabs (Pill Style)
.c-stickers-nav {
  display: inline-flex;
  height: 44px;
  align-items: center;
  justify-content: center;
  background-color: $gray-100;
  border-radius: 12px;
  padding: 4px;
  gap: 0;
  flex-wrap: wrap;
  overflow-x: auto;
}

.c-stickers-nav-link {
  display: inline-flex;
  height: calc(100% - 2px);
  align-items: center;
  justify-content: center;
  padding: 6px 24px;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: $gray-600;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  user-select: none;

  &:hover:not(:disabled) {
    color: $gray-900;
    background-color: rgba(255, 255, 255, 0.5);
  }

  &--active {
    background-color: white;
    color: $gray-900;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Organization Filter
.c-stickers-org-filter {
  background-color: #f0f9fb;
  border-top: 1px solid #e6e6e6;
  border-bottom: 1px solid #e6e6e6;
  padding: 0.75rem 1.5rem;

  &__content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__label {
    font-weight: 500;
    color: $gray-600;
  }

  &__name {
    font-weight: 600;
    color: $gray-900;
  }

  &__clear {
    margin-left: auto;
  }
}

.c-stickers-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

// Filters Section
.c-stickers-filters-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.c-stickers-filters__bulk-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.c-stickers-filters__selected-count {
  font-weight: 500;
  color: $gray-700;
  font-size: 0.875rem;
}

.c-stickers-filters__bulk-select {
  width: 180px;
}

.c-stickers-filters__controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.c-stickers-filters__search {
  width: 200px;
}

.c-stickers-filters__search-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.c-stickers-filters__clear {
  border: 1px solid $gray-300;
  color: $gray-700;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;

  &:hover {
    background-color: $color-purple-text;
    color: white;
    border-color: $color-purple-text;
  }
}

.c-stickers-filters__sort {
  width: 180px;
}

// Table Section
.c-stickers-table-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;
  overflow: hidden;

  :deep(.el-table) {
    min-width: 100%;
    border-radius: 0;

    .cell {
      color: $gray-700;
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
      padding: 0.5rem 0.5rem;
    }

    th > .cell {
      text-transform: capitalize;
      color: $gray-700;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      padding: 0.5rem 0.5rem;
    }

    th.el-table__cell.is-leaf {
      background-color: $gray-50;
      border-bottom: 1px solid $gray-100;
      padding: 0.5rem 0;
    }

    td.el-table__cell {
      border-bottom: 1px solid $gray-100;
      padding: 0;
      vertical-align: middle !important;

      > .cell {
        display: flex;
        align-items: center;
        min-height: 70px;
        padding: 0.625rem 0.5rem;
      }
    }

    tbody tr {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: $gray-50;
      }
    }

    .el-table__empty-block {
      min-height: 400px;
    }
  }
}

// Pagination
.c-stickers-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-top: 1px solid $gray-100;
  flex-wrap: wrap;
  gap: 1rem;

  &__page-size {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__page-size-label {
    font-weight: 500;
    color: $gray-700;
    font-size: 0.875rem;
  }

  &__page-size-select {
    width: 80px;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }

  &__button {
    border-radius: 0.375rem;
    font-weight: 500;
    transition: all 0.15s ease;
    background-color: transparent;
    border: 1px solid $gray-300;
    color: $gray-700;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;

    &:hover:not(:disabled) {
      background-color: transparent;
      border-color: $gray-500;
      color: $gray-900;
    }

    &:disabled {
      border-color: $gray-300;
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    min-width: 140px;
  }

  &__page-number {
    font-size: 0.875rem;
    font-weight: 600;
    color: $gray-900;
  }

  &__item-count {
    font-size: 0.75rem;
    color: $gray-600;
  }
}

// Skeleton Loader
.c-skeleton-loader {
  width: 100%;
  padding: 1rem 0;
}

.c-skeleton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  gap: 1rem;
  border-bottom: 1px solid $gray-100;

  &:last-child {
    border-bottom: none;
  }
}

.c-skeleton-cell {
  height: 20px;
  background: linear-gradient(90deg, #F0F0F0 25%, #E0E0E0 50%, #F0F0F0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;

  &--checkbox {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &--image {
    width: 50px;
    height: 50px;
    flex-shrink: 0;
    border-radius: 8px;
  }

  &--title {
    width: 180px;
    flex-shrink: 0;
  }

  &--type {
    width: 100px;
    flex-shrink: 0;
  }

  &--status {
    width: 120px;
    flex-shrink: 0;
  }

  &--link {
    width: 140px;
    flex-shrink: 0;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Table Cell Styles
.sticker-image-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.sticker-image {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
}

.font-black {
  font-weight: 600;
  color: $gray-900;
}

.font-medium {
  font-weight: 500;
  color: $gray-700;
}

.lh-sm {
  line-height: 1.25;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  min-width: 80px;

  &--approved {
    background-color: #8DD8CD;
    color: #333;
  }

  &--pending {
    background-color: #96C9EF;
    color: #333;
  }

  &--archived {
    background-color: #EBEBEB;
    color: #666;
  }
}

.share-link-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.share-link-text {
  font-size: 12px;
  color: $gray-600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.copy-icon {
  color: #909399;
  cursor: pointer;
  font-size: 16px;
  flex-shrink: 0;
  transition: color 0.3s;

  &:hover {
    color: #1fbeaf;
  }
}

// Error State
.c-stickers-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;

  &__icon {
    color: #f56c6c;
    margin-bottom: 1rem;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0 0 0.5rem 0;
  }

  &__text {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0 0 1.5rem 0;
  }

  &__button {
    background-color: #f56c6c !important;
    border-color: #f56c6c !important;
    color: white !important;

    &:hover {
      background-color: #e45656 !important;
      border-color: #e45656 !important;
    }
  }
}

// Responsive Design
@media (max-width: 768px) {
  .c-stickers-page {
    padding: 1.5rem;

    &__heading {
      font-size: 1.5rem;
    }

    &__header {
      gap: 1rem;
    }
  }

  .c-stickers-filters-section {
    flex-direction: column;
    align-items: stretch;
  }

  .c-stickers-filters__controls {
    flex-direction: column;
    align-items: stretch;

    .c-stickers-filters__search,
    .c-stickers-filters__sort {
      width: 100%;
    }
  }

  .c-stickers-pagination {
    flex-direction: column;
    align-items: stretch;

    &__controls {
      flex-direction: column;
      gap: 1rem;
    }

    &__button {
      width: 100%;
    }

    &__info {
      order: -1;
    }
  }
}
</style>
